import {
  auditLogKeys,
  customerKeys,
  customFieldKeys,
  dashboardKeys,
  documentLayoutAssetKeys,
  documentLayoutKeys,
  documentFormLayoutKeys,
  fieldControllerKeys,
  estimatedInvoiceKeys,
  invoiceKeys,
  paymentKeys,
  productKeys,
  quotationKeys,
  reportKeys,
  settingsKeys,
  taxKeys,
} from './cacheKeys';

const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete']);

function toPathname(url = '') {
  if (!url) return '';
  return String(url).split('?')[0];
}

function dedupeQueryKeys(queryKeys = []) {
  const seen = new Set();
  return queryKeys.filter((queryKey) => {
    const serializedKey = JSON.stringify(queryKey || []);
    if (seen.has(serializedKey)) {
      return false;
    }
    seen.add(serializedKey);
    return true;
  });
}

function collectDocumentTargets({ pathname, method, rootKey, listKey, detailKey, auxKeys = [] }) {
  const detailMatch = pathname.match(/^\/([^/]+(?:-invoices)?)\/([^/]+)$/);
  const emailMatch = pathname.match(/^\/([^/]+(?:-invoices)?)\/([^/]+)\/send-email$/);

  if (emailMatch && emailMatch[2]) {
    return dedupeQueryKeys([detailKey(emailMatch[2]), auditLogKeys.root]);
  }

  if (detailMatch && detailMatch[2] && method !== 'post') {
    return dedupeQueryKeys([listKey(), detailKey(detailMatch[2]), ...auxKeys]);
  }

  return dedupeQueryKeys([listKey(), ...auxKeys.length ? auxKeys : [rootKey]]);
}

export function getInvalidationTargetsForMutation(event = {}) {
  const method = String(event.method || '').toLowerCase();
  if (!MUTATION_METHODS.has(method)) {
    return [];
  }

  const pathname = toPathname(event.url);
  if (!pathname) {
    return [];
  }

  if (/^\/invoices(\/|$)/.test(pathname)) {
    return collectDocumentTargets({
      pathname,
      method,
      rootKey: invoiceKeys.root,
      listKey: invoiceKeys.list,
      detailKey: invoiceKeys.detail,
      auxKeys: [dashboardKeys.root, reportKeys.salesBook(), auditLogKeys.root],
    });
  }

  if (/^\/quotation(\/|$)/.test(pathname)) {
    return collectDocumentTargets({
      pathname,
      method,
      rootKey: quotationKeys.root,
      listKey: quotationKeys.list,
      detailKey: quotationKeys.detail,
      auxKeys: [dashboardKeys.root, auditLogKeys.root],
    });
  }

  if (/^\/estimated-invoices(\/|$)/.test(pathname)) {
    return collectDocumentTargets({
      pathname,
      method,
      rootKey: estimatedInvoiceKeys.root,
      listKey: estimatedInvoiceKeys.list,
      detailKey: estimatedInvoiceKeys.detail,
      auxKeys: [dashboardKeys.root, auditLogKeys.root],
    });
  }

  if (/^\/products(\/|$)/.test(pathname)) {
    const match = pathname.match(/^\/products\/([^/]+)$/);
    return dedupeQueryKeys(
      match
        ? [productKeys.list(), productKeys.detail(match[1]), auditLogKeys.root]
        : [productKeys.list(), auditLogKeys.root]
    );
  }

  if (/^\/customers(\/|$)/.test(pathname)) {
    const match = pathname.match(/^\/customers\/([^/]+)$/);
    return dedupeQueryKeys(
      match
        ? [customerKeys.list(), customerKeys.detail(match[1]), auditLogKeys.root]
        : [customerKeys.list(), auditLogKeys.root]
    );
  }

  if (/^\/taxes(\/|$)/.test(pathname)) {
    return dedupeQueryKeys([taxKeys.list(), auditLogKeys.root]);
  }

  if (/^\/settings\/design-preferences(\/|$)/.test(pathname)) {
    return dedupeQueryKeys([
      settingsKeys.designPreferences(),
      dashboardKeys.root,
      auditLogKeys.root,
    ]);
  }

  if (/^\/settings\/ui-visibility(\/|$)/.test(pathname)) {
    return dedupeQueryKeys([
      settingsKeys.visibilityPreferences(),
      dashboardKeys.root,
      auditLogKeys.root,
    ]);
  }

  if (/^\/settings\/showcase(\/|$)/.test(pathname)) {
    return dedupeQueryKeys([settingsKeys.showcase()]);
  }

  if (/^\/settings(\/|$)/.test(pathname)) {
    return dedupeQueryKeys([
      settingsKeys.profile(),
      settingsKeys.designPreferences(),
      settingsKeys.visibilityPreferences(),
      settingsKeys.showcase(),
      dashboardKeys.root,
      auditLogKeys.root,
    ]);
  }

  if (/^\/payments(\/|$)/.test(pathname)) {
    return dedupeQueryKeys([
      paymentKeys.root,
      reportKeys.agingSummary(),
      dashboardKeys.root,
      auditLogKeys.root,
    ]);
  }

  if (/^\/dashboard\/preferences(\/|$)/.test(pathname)) {
    return dedupeQueryKeys([dashboardKeys.preferences(), dashboardKeys.root, auditLogKeys.root]);
  }

  if (/^\/document-layouts(\/|$)/.test(pathname)) {
    const match = pathname.match(/^\/document-layouts\/([^/]+)(?:\/(publish|duplicate))?$/);
    if (match?.[1]) {
      return dedupeQueryKeys([documentLayoutKeys.list(), documentLayoutKeys.detail(match[1])]);
    }
    return dedupeQueryKeys([documentLayoutKeys.list()]);
  }

  if (/^\/document-layout-assets(\/|$)/.test(pathname)) {
    return dedupeQueryKeys([documentLayoutAssetKeys.list()]);
  }

  if (/^\/document-form-layouts(\/|$)/.test(pathname)) {
    const match = pathname.match(/^\/document-form-layouts\/([^/]+)(?:\/(duplicate))?$/);
    if (match?.[1] && match[1] !== 'preferences') {
      return dedupeQueryKeys([
        documentFormLayoutKeys.list(),
        documentFormLayoutKeys.detail(match[1]),
        documentFormLayoutKeys.preferences(),
      ]);
    }
    return dedupeQueryKeys([documentFormLayoutKeys.list(), documentFormLayoutKeys.preferences()]);
  }

  if (/^\/field-controller\/preferences(\/|$)/.test(pathname)) {
    return dedupeQueryKeys([fieldControllerKeys.root, auditLogKeys.root]);
  }

  if (/^\/custom-fields\/definitions(\/|$)/.test(pathname)) {
    return dedupeQueryKeys([
      customFieldKeys.root,
      documentFormLayoutKeys.root,
      auditLogKeys.root,
    ]);
  }

  return [];
}
